import { LitElement, html, css } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { X, Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle, Check } from 'lucide';
import { lucideIcon } from '../icon.js';
import { COORDINATE_SYSTEMS } from '@mv/models';
import { api } from '../api.js';
import {
  BULK_FIELDS, fieldLabel, templateRows, parseCsv, readTable, guessMapping, detectFromMapping,
  buildPlan, formatCoordinate, formatDateNl, jobCodeMismatch,
} from '../utils/bulk-update.js';

/**
 * SheetJS pas laden als het venster opent: het is een flinke bibliotheek die verder nergens in
 * de backoffice nodig is. Eén belofte, zodat twee keer openen niet twee keer laadt.
 */
let xlsxPromise = null;
const loadXlsx = () => (xlsxPromise ||= import('xlsx'));

/** Bestandsgrootte zoals in het prototype: '14 kB'. */
const fmtSize = (n) => (n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${Math.round(n / 1024)} kB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

/**
 * boring-bulk-update-dialog — 'Bulk bijwerken' in de boringentab (Trello BCwaRl2Z).
 *
 * Na het veldwerk meet een landmeter de boringen in, en datum en boormeester worden achteraf
 * aangevuld. Dat ging per boring met de hand. Hier in twee stappen, zoals het prototype:
 *   1. Bestand aanleveren — template downloaden (.xlsx met alle boringnamen) of een eigen
 *      .xlsx/.xls/.csv slepen.
 *   2. Kolommen koppelen — automatisch bij ons template, anders geraden op inhoud; elke
 *      koppeling is met een dropdown aan te passen. Daaronder het voorbeeld (oud → nieuw).
 *
 * Alleen bestaande boringen. Een onbekende naam wordt getoond, nooit aangemaakt; een lege cel
 * laat de bestaande waarde staan. De logica zit in utils/bulk-update.js.
 */
export class BoringBulkUpdateDialog extends LitElement {
  static properties = {
    open: { type: Boolean },
    projectId: { type: String },
    projectCode: { type: String },
    borings: { type: Array },        // actieve boringen van dit project
    coordSystem: { type: String },   // weergave van coördinaten in het voorbeeld
    _step: { state: true },
    _file: { state: true },
    _table: { state: true },
    _mapping: { state: true },
    _coords: { state: true },
    _rowBoring: { state: true },
    _dupPick: { state: true },
    _dragOver: { state: true },
    _busy: { state: true },
    _error: { state: true },
  };

  constructor() {
    super();
    this.open = false;
    this.borings = [];
    this.coordSystem = 'RD';
    this._reset();
  }

  _reset() {
    this._step = 1;
    this._file = null;
    this._table = null;
    this._mapping = [];
    this._coords = { system: null, swapped: false, auto: false };
    this._rowBoring = {};
    this._dupPick = {};
    this._dragOver = false;
    this._busy = false;
    this._error = null;
  }

  updated(changed) {
    if (changed.has('open') && this.open) {
      this._reset();
      loadXlsx().catch(() => {}); // alvast ophalen; fouten komen pas bij gebruik
    }
  }

  _close() {
    if (this._busy) return;
    this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));
  }

  // ── Stap 1 ─────────────────────────────────────────────────────────────────

  async _downloadTemplate() {
    try {
      const XLSX = await loadXlsx();
      const ws = XLSX.utils.aoa_to_sheet(templateRows(this.borings || [], this.coordSystem));
      ws['!cols'] = [{ wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 22 }, { wch: 14 }, { wch: 22 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Boringen');
      const code = (this.projectCode || 'project').replace(/[^\w.-]+/g, '_');
      XLSX.writeFile(wb, `boringen_${code}.xlsx`);
    } catch (e) {
      this._error = `Template maken mislukt: ${e?.message || e}`;
    }
  }

  async _readFile(file) {
    if (!file) return;
    this._error = null;
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!['xlsx', 'xls', 'csv', 'txt'].includes(ext)) {
      this._error = 'Kies een .xlsx, .xls of .csv-bestand.';
      return;
    }
    try {
      let rawRows, jobCode = null;
      if (ext === 'csv' || ext === 'txt') {
        const buf = await file.arrayBuffer();
        let text = new TextDecoder('utf-8').decode(buf);
        // Een veldlijst uit Windows is vaak geen UTF-8; dan krijg je vervangingstekens.
        if (text.includes('�')) text = new TextDecoder('windows-1252').decode(buf);
        ({ rows: rawRows, jobCode } = parseCsv(text));
      } else {
        const XLSX = await loadXlsx();
        const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        rawRows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: '', blankrows: false });
      }
      const table = readTable(rawRows, jobCode);
      if (!table.rows.length) { this._error = 'Dit bestand bevat geen regels.'; return; }
      this._file = { name: file.name, size: file.size };
      this._table = table;
      this._mapping = guessMapping(table, this.borings || []);
      this._redetect();
      this._rowBoring = {};
      this._dupPick = {};
      this._step = 2;
    } catch (e) {
      this._error = `Bestand lezen mislukt: ${e?.message || e}`;
    }
  }

  _redetect() {
    this._coords = detectFromMapping(this._table, this._mapping);
  }

  // ── Stap 2 ─────────────────────────────────────────────────────────────────

  /** Eén veld hoort bij één kolom: wie hetzelfde veld al had, laat het los. */
  _setMapping(i, field) {
    const before = this._mapping[i];
    const next = this._mapping.map((f, k) => (k !== i && field && f === field ? '' : f));
    next[i] = field;
    this._mapping = next;
    if (['x', 'y'].includes(field) || ['x', 'y'].includes(before)) this._redetect();
  }

  _plan() {
    return buildPlan({
      table: this._table, mapping: this._mapping, coords: this._coords,
      borings: this.borings || [], rowBoring: this._rowBoring, dupPick: this._dupPick,
    });
  }

  async _submit(plan) {
    if (!plan.payload.length) return;
    this._busy = true; this._error = null;
    try {
      await api.bulkUpdateBorings(this.projectId, plan.payload);
      this.dispatchEvent(new CustomEvent('updated', {
        detail: { ids: plan.payload.map(p => p.boringId), count: plan.payload.length },
        bubbles: true, composed: true,
      }));
    } catch (e) {
      this._error = e?.message || 'Bijwerken mislukt.';
    } finally {
      this._busy = false;
    }
  }

  // ── Weergave ───────────────────────────────────────────────────────────────

  _renderStep1() {
    const n = (this.borings || []).length;
    return html`
      <div class="tpl">
        <span class="tpl-ico">${unsafeSVG(lucideIcon(FileSpreadsheet, 20))}</span>
        <div class="tpl-txt">
          <b>Begin met het template</b>
          <span>Excel-bestand met ${n === 1 ? 'de boring' : `alle ${n} boringen`} van dit project. Boornummers staan er al in; vul de overige kolommen aan.</span>
        </div>
        <button class="btn ghost" @click=${() => this._downloadTemplate()}>${unsafeSVG(lucideIcon(Download, 14))} Download template</button>
      </div>
      <label class="drop ${this._dragOver ? 'over' : ''}"
        @dragover=${(e) => { e.preventDefault(); this._dragOver = true; }}
        @dragleave=${() => { this._dragOver = false; }}
        @drop=${(e) => { e.preventDefault(); this._dragOver = false; this._readFile(e.dataTransfer?.files?.[0]); }}>
        <input type="file" accept=".xlsx,.xls,.csv" hidden
          @change=${(e) => { this._readFile(e.target.files?.[0]); e.target.value = ''; }}>
        <span class="drop-ico">${unsafeSVG(lucideIcon(Upload, 18))}</span>
        <b>Sleep je bestand hierheen</b>
        <span>of <em>kies een bestand</em> · .xlsx, .xls of .csv</span>
      </label>`;
  }

  _fmtValue(field, v) {
    if (field === 'xy') return v && v[0] != null ? formatCoordinate(v[0], v[1], this.coordSystem) : '—';
    if (v == null || v === '') return '—';
    if (field === 'z') return `${Number(v).toFixed(2)} m`;
    if (field === 'datum') return formatDateNl(v);
    return String(v);
  }

  _renderMapping() {
    const t = this._table;
    const sample = (i) => t.rows.map(r => r[i]).filter(v => v !== '' && v != null).slice(0, 3).join(' · ');
    return html`
      <table class="map">
        <thead><tr><th>Kolom in bestand</th><th>Voorbeeld</th><th>Koppelen aan</th></tr></thead>
        <tbody>
          ${t.columns.map((c, i) => html`
            <tr>
              <td><b>${c}</b></td>
              <td class="muted">${sample(i)}</td>
              <td>
                <select .value=${this._mapping[i] || ''} @change=${(e) => this._setMapping(i, e.target.value)}>
                  ${['', ...BULK_FIELDS].map(f => html`<option value=${f} ?selected=${(this._mapping[i] || '') === f}>${fieldLabel(f, this._coords.system)}</option>`)}
                </select>
              </td>
            </tr>`)}
        </tbody>
      </table>`;
  }

  _renderCoords() {
    const hasXY = this._mapping.includes('x') && this._mapping.includes('y');
    if (!hasXY) return '';
    const sys = this._coords.system;
    return html`
      <div class="sysrow ${sys ? '' : 'warn'}">
        ${sys ? html`<span>Stelsel ${this._coords.auto ? 'herkend' : ''}:</span>`
              : html`${unsafeSVG(lucideIcon(AlertTriangle, 14))}<span>Stelsel niet herkend — kies het stelsel van dit bestand:</span>`}
        <select .value=${sys || ''} @change=${(e) => { this._coords = { system: e.target.value || null, swapped: false, auto: false }; }}>
          ${sys ? '' : html`<option value="">Kies…</option>`}
          ${Object.values(COORDINATE_SYSTEMS).map(d => html`<option value=${d.id} ?selected=${d.id === sys}>${d.label}</option>`)}
        </select>
      </div>`;
  }

  _renderPreview(plan) {
    const labels = { xy: 'Coördinaat', z: 'Maaiveld', datum: 'Datum', boormeester: 'Boormeester' };
    const withChanges = plan.entries.filter(e => e.changes.length || e.errors.length);
    const unchanged = plan.entries.length - withChanges.length;
    return html`
      <h4>Voorbeeld</h4>
      ${withChanges.length ? html`
        <table class="prev">
          <thead><tr><th>Boring</th><th>Wijziging (oud → nieuw)</th></tr></thead>
          <tbody>
            ${withChanges.map(e => html`
              <tr>
                <td>
                  <b>${e.boring.name}</b>
                  ${e.match.kind === 'normalized' ? html`<div class="hint">“${e.key}” herkend als ${e.boring.name}</div>` : ''}
                </td>
                <td>
                  ${e.changes.map(c => html`
                    <div class="chg"><span class="lbl">${labels[c.field]}</span>
                      <span class="old">${this._fmtValue(c.field, c.from)}</span> → <span class="new">${this._fmtValue(c.field, c.to)}</span></div>`)}
                  ${e.errors.map(err => html`<div class="rowerr">${unsafeSVG(lucideIcon(AlertTriangle, 12))} ${err} — dit veld wordt niet opgeslagen</div>`)}
                </td>
              </tr>`)}
          </tbody>
        </table>` : html`<p class="muted">Geen wijzigingen gevonden.</p>`}
      ${unchanged ? html`<p class="muted small">${unchanged} ${unchanged === 1 ? 'boring is' : 'boringen zijn'} al gelijk aan het bestand.</p>` : ''}

      ${plan.duplicates.length ? html`
        <h4 class="warnh">Dubbel in het bestand <span>kies per boring één regel</span></h4>
        ${plan.duplicates.map(d => html`
          <div class="choice">
            <b>${d.boring.name}</b>
            ${d.rows.map(r => html`
              <label class="radio">
                <input type="radio" name="dup-${d.boring.id}" .checked=${d.chosen === r.rowIndex}
                  @change=${() => { this._dupPick = { ...this._dupPick, [d.boring.id]: r.rowIndex }; }}>
                <span>regel ${r.rowIndex + 1}: ${this._rowSummary(r)}</span>
              </label>`)}
          </div>`)}` : ''}

      ${plan.ambiguous.length ? html`
        <h4 class="warnh">Meerdere boringen passen <span>kies de juiste boring</span></h4>
        ${plan.ambiguous.map(r => html`
          <div class="choice">
            <b>“${r.key}”</b>
            <select @change=${(e) => { this._rowBoring = { ...this._rowBoring, [r.rowIndex]: e.target.value }; }}>
              <option value="">Kies…</option>
              ${r.match.borings.map(b => html`<option value=${b.id} ?selected=${this._rowBoring[r.rowIndex] === b.id}>${b.name}</option>`)}
            </select>
          </div>`)}` : ''}

      ${plan.unmatched.length ? html`
        <h4>Regels zonder boring <span>worden niet aangemaakt</span></h4>
        <p class="muted small">${plan.unmatched.map(r => r.key).join(', ')}</p>` : ''}

      ${plan.missing.length ? html`
        <h4>Boringen zonder regel <span>blijven ongewijzigd</span></h4>
        <p class="muted small">${plan.missing.map(b => b.name).join(', ')}</p>` : ''}`;
  }

  _rowSummary(r) {
    const v = r.values;
    const parts = [];
    if (v.x != null) parts.push(formatCoordinate(v.x, v.y, this.coordSystem));
    if (v.z != null) parts.push(`${v.z.toFixed(2)} m`);
    if (v.datum) parts.push(formatDateNl(v.datum));
    if (v.boormeester) parts.push(v.boormeester);
    return parts.join(' · ') || 'geen waarden';
  }

  _renderStep2() {
    const t = this._table;
    const plan = this._plan();
    const mismatch = jobCodeMismatch(t.jobCode, this.projectCode);
    return {
      plan,
      body: html`
        <div class="filecard">
          <span class="tpl-ico">${unsafeSVG(lucideIcon(FileSpreadsheet, 18))}</span>
          <div class="tpl-txt">
            <b>${this._file.name}</b>
            <span>${fmtSize(this._file.size)} · ${t.rows.length} rijen · ${t.columns.length} kolommen</span>
          </div>
          <button class="link" @click=${() => { this._step = 1; }}>Ander bestand</button>
        </div>
        ${t.templateSystem ? html`
          <div class="note ok">${unsafeSVG(lucideIcon(CheckCircle2, 14))} GeoVoice-template herkend. Alle kolommen zijn automatisch gekoppeld.</div>` : ''}
        ${mismatch ? html`
          <div class="note warn">${unsafeSVG(lucideIcon(AlertTriangle, 14))}
            Dit bestand hoort bij job <b>${t.jobCode}</b>, dit project heeft code <b>${this.projectCode}</b>. Controleer of je het goede bestand hebt.</div>` : ''}
        ${!this._mapping.includes('name') ? html`
          <div class="note warn">${unsafeSVG(lucideIcon(AlertTriangle, 14))} Geen kolom met boornummers gevonden. Koppel hieronder de kolom met de boringnamen.</div>` : ''}
        ${this._renderMapping()}
        ${this._renderCoords()}
        ${this._mapping.includes('name') ? this._renderPreview(plan) : ''}`,
    };
  }

  render() {
    if (!this.open) return '';
    const step2 = this._step === 2 && this._table ? this._renderStep2() : null;
    const n = step2?.plan.payload.length || 0;
    const stepDot = (k, label) => html`
      <span class="step ${this._step === k ? 'on' : this._step > k ? 'done' : ''}">
        <span class="dot">${this._step > k ? unsafeSVG(lucideIcon(Check, 11)) : k}</span>${label}
      </span>`;
    return html`
      <div class="back" @click=${(e) => { if (e.target === e.currentTarget) this._close(); }}
        @keydown=${(e) => e.key === 'Escape' && this._close()}>
        <div class="modal" role="dialog" aria-label="Boringen bijwerken">
          <div class="head">
            <div>
              <h3>Boringen bijwerken</h3>
              <span class="sub">Werk coördinaten, maaiveldhoogte, datum en boormeester van bestaande boringen in één keer bij vanuit Excel of CSV.</span>
            </div>
            <button class="x" @click=${() => this._close()} aria-label="Sluiten">${unsafeSVG(lucideIcon(X, 18))}</button>
          </div>
          <div class="steps">${stepDot(1, 'Bestand aanleveren')}<span class="line"></span>${stepDot(2, 'Kolommen koppelen')}</div>
          ${this._error ? html`<div class="err">${this._error}</div>` : ''}
          <div class="body">${step2 ? step2.body : this._renderStep1()}</div>
          <div class="foot">
            ${step2 ? html`<button class="btn ghost plain" @click=${() => { this._step = 1; }} ?disabled=${this._busy}>Terug</button>` : html`<span></span>`}
            <div class="foot-right">
              <button class="btn ghost" @click=${() => this._close()} ?disabled=${this._busy}>Annuleren</button>
              ${step2
                ? html`<button class="btn" ?disabled=${this._busy || !n} @click=${() => this._submit(step2.plan)}>
                    ${this._busy ? 'Bijwerken…' : `${n} ${n === 1 ? 'boring' : 'boringen'} bijwerken`}</button>`
                : html`<button class="btn" disabled>Volgende</button>`}
            </div>
          </div>
        </div>
      </div>`;
  }

  static styles = css`
    :host { display: contents; }
    .back { position: fixed; inset: 0; z-index: var(--mv-z-modal, 1200); background: rgba(15, 23, 42, .45);
      display: flex; align-items: center; justify-content: center; padding: 4vh 1rem; }
    .modal { width: min(640px, 100%); max-height: 92vh; background: var(--mv-surface); border-radius: .8rem;
      box-shadow: 0 24px 60px rgba(0,0,0,.25); display: flex; flex-direction: column; overflow: hidden; }
    .head { display: flex; align-items: flex-start; gap: 12px; padding: 22px 24px 0; }
    .head h3 { margin: 0; font-size: 1.05rem; font-weight: 800; color: var(--mv-text); }
    .head .sub { font-size: .8rem; color: var(--mv-text-muted); }
    .x { margin-left: auto; display: inline-flex; background: none; border: none; cursor: pointer; color: var(--mv-text-sub); padding: 4px; }
    .steps { display: flex; align-items: center; gap: 10px; padding: 12px 24px 14px; border-bottom: 1px solid var(--mv-border); }
    .step { display: inline-flex; align-items: center; gap: 6px; font-size: .8rem; font-weight: 600; color: var(--mv-text-muted); }
    .step.on { color: var(--mv-text); }
    .dot { width: 18px; height: 18px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center;
      font-size: .7rem; font-weight: 700; background: var(--mv-border); color: var(--mv-text-sub); }
    .step.on .dot { background: var(--mv-primary); color: #fff; }
    .step.done .dot { background: var(--mv-primary-tint, rgba(75,157,53,.12)); color: var(--mv-primary); }
    .line { width: 40px; height: 1px; background: var(--mv-border); }
    .err { margin: .8rem 24px 0; padding: 8px 11px; border-radius: 7px; font-size: .82rem;
      color: var(--mv-danger, #B91C1C); background: var(--mv-danger-bg, #FEE2E2); }
    .body { padding: 18px 24px; display: flex; flex-direction: column; gap: 12px; overflow-y: auto; min-height: 280px; }

    .tpl, .filecard { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border: 1px solid var(--mv-border); border-radius: 10px; }
    .tpl-ico { display: inline-flex; padding: 8px; border-radius: 8px; color: var(--mv-primary); background: var(--mv-primary-tint, rgba(75,157,53,.08)); }
    .tpl-txt { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; font-size: .8rem; color: var(--mv-text-sub); }
    .tpl-txt b { color: var(--mv-text); font-size: .85rem; }
    .drop { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 36px 16px; cursor: pointer;
      border: 1.5px dashed var(--mv-border-strong, #cfd8dc); border-radius: 10px; font-size: .8rem; color: var(--mv-text-sub); }
    .drop.over { border-color: var(--mv-primary); background: var(--mv-primary-tint, rgba(75,157,53,.06)); }
    .drop b { color: var(--mv-text); font-size: .86rem; }
    .drop em { font-style: normal; font-weight: 700; color: var(--mv-primary); }
    .drop-ico { display: inline-flex; padding: 9px; border-radius: 50%; border: 1px solid var(--mv-border); color: var(--mv-text-sub); }

    .note { display: flex; align-items: center; gap: 8px; padding: 8px 11px; border-radius: 7px; font-size: .8rem; }
    .note.ok { color: var(--mv-primary); background: var(--mv-primary-tint, rgba(75,157,53,.08)); }
    .note.warn { color: #92400E; background: #FEF3C7; }
    table { width: 100%; border-collapse: collapse; font-size: .8rem; }
    th { text-align: left; font-size: .68rem; text-transform: uppercase; letter-spacing: .04em; color: var(--mv-text-muted);
      padding: 7px 10px; border-bottom: 1px solid var(--mv-border); font-weight: 700; }
    td { padding: 7px 10px; border-bottom: 1px solid var(--mv-border); vertical-align: top; color: var(--mv-text); }
    .map td:nth-child(2) { max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    select { width: 100%; padding: 6px 8px; border: 1px solid var(--mv-border); border-radius: 7px; font: inherit; font-size: .8rem;
      background: var(--mv-surface); color: var(--mv-text); }
    .sysrow { display: flex; align-items: center; gap: 8px; font-size: .8rem; color: var(--mv-text-sub); }
    .sysrow select { width: auto; }
    .sysrow.warn { color: #92400E; }
    h4 { margin: 8px 0 0; font-size: .84rem; font-weight: 800; color: var(--mv-text); display: flex; align-items: baseline; gap: 8px; }
    h4 span { font-size: .74rem; font-weight: 500; color: var(--mv-text-muted); }
    h4.warnh { color: #92400E; }
    .muted { color: var(--mv-text-muted); }
    .small { margin: 0; font-size: .78rem; line-height: 1.5; }
    .hint { font-size: .72rem; color: var(--mv-text-muted); }
    .chg { display: flex; gap: 6px; align-items: baseline; flex-wrap: wrap; }
    .chg .lbl { min-width: 84px; color: var(--mv-text-muted); }
    .chg .old { color: var(--mv-text-muted); text-decoration: line-through; }
    .chg .new { font-weight: 600; }
    .rowerr { display: flex; align-items: center; gap: 4px; color: var(--mv-danger, #B91C1C); font-size: .76rem; }
    .choice { display: flex; flex-direction: column; gap: 4px; padding: 8px 10px; border: 1px solid #FCD34D; border-radius: 8px; font-size: .8rem; }
    .choice select { width: auto; align-self: flex-start; }
    .radio { display: flex; align-items: center; gap: 6px; cursor: pointer; }
    .link { background: none; border: none; font: inherit; font-size: .8rem; font-weight: 700; color: var(--mv-primary); cursor: pointer; }

    .foot { display: flex; justify-content: space-between; align-items: center; gap: 8px; padding: 14px 24px 20px; border-top: 1px solid var(--mv-border); }
    .foot-right { display: flex; gap: 8px; }
    .btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 15px; border: none; border-radius: 8px;
      background: var(--mv-primary); color: #fff; font: inherit; font-size: .82rem; font-weight: 700; cursor: pointer; white-space: nowrap; }
    .btn:disabled { opacity: .55; cursor: default; }
    .btn.ghost { background: var(--mv-surface); color: var(--mv-text); border: 1px solid var(--mv-border); }
    .btn.plain { border: none; color: var(--mv-text-sub); }
  `;
}

customElements.define('boring-bulk-update-dialog', BoringBulkUpdateDialog);
